/**
 * Copyright © 2026 Delicious AI, LLC
 */

import {
  Box,
  CircularProgress,
  SxProps,
  Theme,
  Typography,
} from '@mui/material'
import React from 'react'

import { getRenderedBoxRect, NaturalImageSize } from './classificationUtils'
import type { ClassificationBoundingBox } from './classificationTypes'

type ViewerBox = Pick<
  ClassificationBoundingBox,
  'id' | 'minX' | 'maxX' | 'minY' | 'maxY'
> & {
  color?: string
  variant?: 'default' | 'spotlight'
  spotlightOverlayColor?: string
  borderRadius?: number
  borderWidth?: number
  fillColor?: string
  cursor?: string
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

export type PanZoomImageViewerHandle = {
  reset: () => void
  focusBox: (
    box: Pick<ClassificationBoundingBox, 'minX' | 'maxX' | 'minY' | 'maxY'>,
  ) => void
}

export type PanZoomPointerInfo = {
  event:
    | React.MouseEvent<HTMLDivElement>
    | React.PointerEvent<HTMLDivElement>
    | React.WheelEvent<HTMLDivElement>
  imageX: number
  imageY: number
  normalizedX: number
  normalizedY: number
  withinImage: boolean
  ctrlKey: boolean
  buttons: number
  isPanGesture: boolean
}

export type PanZoomImageViewerRenderContext = {
  naturalSize: NaturalImageSize | null
  containerSize: {
    width: number
    height: number
  }
  renderedSize: {
    width: number
    height: number
  }
  pan: {
    x: number
    y: number
  }
  zoom: number
  baseScale: number
}

type PanZoomImageViewerProps = {
  imageUrl: string
  imageLoadFailedMessage?: string
  boxes?: ViewerBox[]
  autoFocusBox?: Pick<
    ClassificationBoundingBox,
    'id' | 'minX' | 'maxX' | 'minY' | 'maxY'
  > | null
  height?: number | string
  fitGutterPx?: number
  defaultZoom?: number
  minZoom?: number
  maxZoom?: number
  backgroundSx?: SxProps<Theme>
  resetKey?: string | null
  panMode?: 'always' | 'ctrl' | 'right'
  idleCursor?: string
  renderImageOverlay?: (
    context: PanZoomImageViewerRenderContext,
  ) => React.ReactNode
  renderViewportOverlay?: (
    context: PanZoomImageViewerRenderContext,
  ) => React.ReactNode
  onViewerMouseDown?: (info: PanZoomPointerInfo) => void
  onViewerMouseMove?: (info: PanZoomPointerInfo) => void
  onViewerMouseUp?: (info: PanZoomPointerInfo) => void
}

const getFullSizeImageUrl = (url: string) =>
  url.includes('googleusercontent.com') && !url.endsWith('=s0')
    ? `${url}=s0`
    : url

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

const PanZoomImageViewer = React.forwardRef<
  PanZoomImageViewerHandle,
  PanZoomImageViewerProps
>(
  (
    {
      imageUrl,
      imageLoadFailedMessage = 'Image failed to load.',
      boxes = [],
      autoFocusBox = null,
      height = 520,
      fitGutterPx = 24,
      defaultZoom = 1,
      minZoom = 0.4,
      maxZoom = 4,
      backgroundSx,
      resetKey,
      panMode = 'always',
      idleCursor = 'default',
      renderImageOverlay,
      renderViewportOverlay,
      onViewerMouseDown,
      onViewerMouseMove,
      onViewerMouseUp,
    },
    ref,
  ) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null)
    const [loadedImage, setLoadedImage] = React.useState<{
      url: string
      size: NaturalImageSize
    } | null>(null)
    const [imageLoadFailed, setImageLoadFailed] = React.useState(false)
    const [useFullSizeImage, setUseFullSizeImage] = React.useState(true)
    const [containerSize, setContainerSize] = React.useState({
      width: 0,
      height: 0,
    })
    const [zoom, setZoom] = React.useState(defaultZoom)
    const [pan, setPan] = React.useState({ x: 0, y: 0 })
    const dragState = React.useRef({
      active: false,
      pointerId: null as number | null,
      startX: 0,
      startY: 0,
      initialX: 0,
      initialY: 0,
    })
    const autoFocusedBoxIdRef = React.useRef<string | null>(null)
    const imageElementRef = React.useRef<HTMLImageElement | null>(null)
    const [ctrlPressed, setCtrlPressed] = React.useState(false)

    React.useEffect(() => {
      if (panMode !== 'ctrl') return

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Control') {
          setCtrlPressed(true)
        }
      }
      const handleKeyUp = (event: KeyboardEvent) => {
        if (event.key === 'Control') {
          setCtrlPressed(false)
          dragState.current.active = false
          dragState.current.pointerId = null
        }
      }

      window.addEventListener('keydown', handleKeyDown)
      window.addEventListener('keyup', handleKeyUp)
      return () => {
        window.removeEventListener('keydown', handleKeyDown)
        window.removeEventListener('keyup', handleKeyUp)
      }
    }, [panMode])

    React.useEffect(() => {
      if (!containerRef.current) return

      let timeoutId = 0
      let rafId = 0

      const updateSize = () => {
        if (!containerRef.current) return
        const rect = containerRef.current.getBoundingClientRect()
        setContainerSize({
          width: rect.width,
          height: rect.height,
        })
      }

      updateSize()
      rafId = window.requestAnimationFrame(updateSize)
      timeoutId = window.setTimeout(updateSize, 120)

      const observer = new ResizeObserver(entries => {
        const entry = entries[0]
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        })
      })
      observer.observe(containerRef.current)
      window.addEventListener('resize', updateSize)
      return () => {
        observer.disconnect()
        window.removeEventListener('resize', updateSize)
        window.cancelAnimationFrame(rafId)
        window.clearTimeout(timeoutId)
      }
    }, [])

    const fullSizeImageUrl = getFullSizeImageUrl(imageUrl)
    const displayImageUrl = useFullSizeImage ? fullSizeImageUrl : imageUrl
    // Key size to the active URL so a cached onLoad cannot be wiped by a later
    // reset effect (which previously left the viewer stuck on the spinner).
    const naturalSize =
      loadedImage?.url === displayImageUrl ? loadedImage.size : null

    const applyLoadedImageSize = React.useCallback(
      (img: HTMLImageElement, url: string) => {
        if (!img.naturalWidth || !img.naturalHeight) return
        setImageLoadFailed(false)
        setLoadedImage({
          url,
          size: {
            width: img.naturalWidth,
            height: img.naturalHeight,
          },
        })
      },
      [],
    )

    React.useEffect(() => {
      setZoom(defaultZoom)
      setPan({ x: 0, y: 0 })
      setImageLoadFailed(false)
      setUseFullSizeImage(true)
      autoFocusedBoxIdRef.current = null
    }, [defaultZoom, imageUrl, resetKey])

    React.useLayoutEffect(() => {
      const img = imageElementRef.current
      if (img?.complete) {
        applyLoadedImageSize(img, displayImageUrl)
      }
    }, [applyLoadedImageSize, displayImageUrl, resetKey])

    const baseScale = React.useMemo(() => {
      if (!naturalSize || !containerSize.width || !containerSize.height) {
        return 1
      }

      const availableWidth = Math.max(containerSize.width - fitGutterPx * 2, 1)
      const availableHeight = Math.max(
        containerSize.height - fitGutterPx * 2,
        1,
      )

      return Math.min(
        availableWidth / naturalSize.width,
        availableHeight / naturalSize.height,
      )
    }, [containerSize.height, containerSize.width, fitGutterPx, naturalSize])

    const renderedSize = React.useMemo(() => {
      if (!naturalSize) {
        return { width: 0, height: 0 }
      }

      return {
        width: naturalSize.width * baseScale * zoom,
        height: naturalSize.height * baseScale * zoom,
      }
    }, [baseScale, naturalSize, zoom])

    const applyWheelZoom = React.useCallback(
      (nextZoom: number, clientX: number, clientY: number) => {
        const clampedZoom = clamp(Number(nextZoom.toFixed(2)), minZoom, maxZoom)

        if (
          !containerRef.current ||
          !naturalSize ||
          renderedSize.width === 0 ||
          renderedSize.height === 0
        ) {
          setZoom(clampedZoom)
          return
        }

        const rect = containerRef.current.getBoundingClientRect()
        const containerCenterX = rect.width / 2
        const containerCenterY = rect.height / 2
        const centeredAnchorX = clientX - rect.left - containerCenterX
        const centeredAnchorY = clientY - rect.top - containerCenterY
        const nextRenderedWidth = naturalSize.width * baseScale * clampedZoom
        const nextRenderedHeight = naturalSize.height * baseScale * clampedZoom
        const relativeX = (centeredAnchorX - pan.x) / renderedSize.width
        const relativeY = (centeredAnchorY - pan.y) / renderedSize.height

        setZoom(clampedZoom)
        setPan({
          x: centeredAnchorX - relativeX * nextRenderedWidth,
          y: centeredAnchorY - relativeY * nextRenderedHeight,
        })
      },
      [baseScale, maxZoom, minZoom, naturalSize, pan.x, pan.y, renderedSize],
    )

    const toPointerInfo = React.useCallback(
      (
        event:
          | React.MouseEvent<HTMLDivElement>
          | React.PointerEvent<HTMLDivElement>
          | React.WheelEvent<HTMLDivElement>,
      ): PanZoomPointerInfo | null => {
        if (!containerRef.current || !naturalSize || !renderedSize.width) {
          return null
        }

        const rect = containerRef.current.getBoundingClientRect()
        const centeredX = event.clientX - rect.left - rect.width / 2
        const centeredY = event.clientY - rect.top - rect.height / 2
        const renderedX = centeredX - pan.x + renderedSize.width / 2
        const renderedY = centeredY - pan.y + renderedSize.height / 2
        const withinImage =
          renderedX >= 0 &&
          renderedX <= renderedSize.width &&
          renderedY >= 0 &&
          renderedY <= renderedSize.height
        const imageX = clamp(
          (renderedX / Math.max(renderedSize.width, 1)) * naturalSize.width,
          0,
          naturalSize.width,
        )
        const imageY = clamp(
          (renderedY / Math.max(renderedSize.height, 1)) * naturalSize.height,
          0,
          naturalSize.height,
        )

        return {
          event,
          imageX,
          imageY,
          normalizedX: imageX / Math.max(naturalSize.width, 1),
          normalizedY: imageY / Math.max(naturalSize.height, 1),
          withinImage,
          ctrlKey: event.ctrlKey,
          buttons:
            'buttons' in event && typeof event.buttons === 'number'
              ? event.buttons
              : 0,
          isPanGesture: false,
        }
      },
      [naturalSize, pan.x, pan.y, renderedSize.height, renderedSize.width],
    )

    const focusBox = React.useCallback(
      (
        box: Pick<ClassificationBoundingBox, 'minX' | 'maxX' | 'minY' | 'maxY'>,
      ) => {
        if (!naturalSize || !containerSize.width || !containerSize.height) {
          return
        }

        const baseRenderedSize = {
          width: naturalSize.width * baseScale,
          height: naturalSize.height * baseScale,
        }
        const rect = getRenderedBoxRect(box, naturalSize, baseRenderedSize)
        const fitWidthZoom = containerSize.width / (rect.width * 2.2)
        const fitHeightZoom = containerSize.height / (rect.height * 2.2)
        const focusZoom = clamp(
          Math.min(fitWidthZoom, fitHeightZoom),
          1.35,
          maxZoom,
        )
        const focusRenderedWidth = naturalSize.width * baseScale * focusZoom
        const focusRenderedHeight = naturalSize.height * baseScale * focusZoom
        const centerX =
          (rect.left + rect.width / 2) / Math.max(baseRenderedSize.width, 1)
        const centerY =
          (rect.top + rect.height / 2) / Math.max(baseRenderedSize.height, 1)

        setZoom(focusZoom)
        setPan({
          x: (0.5 - centerX) * focusRenderedWidth,
          y: (0.5 - centerY) * focusRenderedHeight,
        })
      },
      [
        baseScale,
        containerSize.height,
        containerSize.width,
        maxZoom,
        naturalSize,
      ],
    )

    React.useImperativeHandle(
      ref,
      () => ({
        reset: () => {
          setZoom(defaultZoom)
          setPan({ x: 0, y: 0 })
        },
        focusBox,
      }),
      [defaultZoom, focusBox],
    )

    React.useEffect(() => {
      if (!autoFocusBox) {
        autoFocusedBoxIdRef.current = null
        return
      }

      if (
        !naturalSize ||
        !containerSize.width ||
        !containerSize.height ||
        autoFocusedBoxIdRef.current === autoFocusBox.id
      ) {
        return
      }

      focusBox(autoFocusBox)
      autoFocusedBoxIdRef.current = autoFocusBox.id
    }, [
      autoFocusBox,
      containerSize.height,
      containerSize.width,
      focusBox,
      naturalSize,
    ])

    React.useEffect(() => {
      const el = containerRef.current
      if (!el) return
      const handler = (event: WheelEvent) => {
        event.preventDefault()
        event.stopPropagation()
        const zoomMultiplier = event.deltaY < 0 ? 1.22 : 1 / 1.22
        applyWheelZoom(zoom * zoomMultiplier, event.clientX, event.clientY)
      }
      el.addEventListener('wheel', handler, { passive: false })
      return () => el.removeEventListener('wheel', handler)
    }, [applyWheelZoom, zoom])

    const renderContext: PanZoomImageViewerRenderContext = {
      naturalSize,
      containerSize,
      renderedSize,
      pan,
      zoom,
      baseScale,
    }
    const baseViewerSx: SxProps<Theme> = (theme: Theme) => ({
      position: 'relative',
      height,
      overflow: 'hidden',
      borderRadius: 2,
      border: '1px solid',
      borderColor: theme.palette.divider,
      background:
        theme.palette.mode === 'dark'
          ? 'radial-gradient(circle at top, rgba(51,65,85,0.9), rgba(15,23,42,1))'
          : 'radial-gradient(circle at top, #e2e8f0, #cbd5e1)',
      cursor:
        panMode === 'ctrl'
          ? ctrlPressed && zoom > 1
            ? 'grab'
            : idleCursor
          : panMode === 'right'
            ? idleCursor
            : zoom > 1
              ? 'grab'
              : idleCursor,
      touchAction: 'none',
      userSelect: 'none',
    })
    const viewerSx: SxProps<Theme> = backgroundSx
      ? ([baseViewerSx, backgroundSx] as SxProps<Theme>)
      : baseViewerSx

    const isPanGesture = (
      event:
        | React.MouseEvent<HTMLDivElement>
        | React.PointerEvent<HTMLDivElement>,
      button = event.button,
    ) =>
      panMode === 'always' ||
      (panMode === 'ctrl' && event.ctrlKey) ||
      (panMode === 'right' && (button === 2 || event.ctrlKey))

    const stopDrag = () => {
      dragState.current.active = false
      dragState.current.pointerId = null
    }

    return (
      <Box
        ref={containerRef}
        sx={viewerSx}
        onDragStart={event => event.preventDefault()}
        onPointerDown={(event: React.PointerEvent<HTMLDivElement>) => {
          const pointerInfo = toPointerInfo(event)
          const panActive = isPanGesture(event)
          if (pointerInfo) {
            onViewerMouseDown?.({
              ...pointerInfo,
              isPanGesture: panActive,
            })
          }
          if (!panActive) {
            return
          }
          event.preventDefault()
          event.currentTarget.setPointerCapture(event.pointerId)
          dragState.current = {
            active: true,
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            initialX: pan.x,
            initialY: pan.y,
          }
        }}
        onPointerMove={(event: React.PointerEvent<HTMLDivElement>) => {
          const pointerInfo = toPointerInfo(event)
          const panActive =
            dragState.current.active ||
            isPanGesture(
              event,
              event.button === 0 && event.buttons === 2 ? 2 : event.button,
            )
          if (!dragState.current.active) {
            if (pointerInfo) {
              onViewerMouseMove?.({
                ...pointerInfo,
                isPanGesture: panActive,
              })
            }
            return
          }
          if (dragState.current.pointerId !== event.pointerId) {
            return
          }
          event.preventDefault()
          if (pointerInfo) {
            onViewerMouseMove?.({
              ...pointerInfo,
              isPanGesture: true,
            })
          }
          setPan({
            x:
              dragState.current.initialX +
              (event.clientX - dragState.current.startX),
            y:
              dragState.current.initialY +
              (event.clientY - dragState.current.startY),
          })
        }}
        onPointerUp={(event: React.PointerEvent<HTMLDivElement>) => {
          const panActive = dragState.current.active || isPanGesture(event)
          const capturedPointerId = dragState.current.pointerId
          stopDrag()
          if (
            capturedPointerId === event.pointerId &&
            event.currentTarget.hasPointerCapture(event.pointerId)
          ) {
            event.currentTarget.releasePointerCapture(event.pointerId)
          }
          const pointerInfo = toPointerInfo(event)
          if (pointerInfo) {
            onViewerMouseUp?.({
              ...pointerInfo,
              isPanGesture: panActive,
            })
          }
        }}
        onPointerCancel={(event: React.PointerEvent<HTMLDivElement>) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId)
          }
          stopDrag()
        }}
        onContextMenu={event => {
          if (panMode === 'right') {
            event.preventDefault()
          }
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: renderedSize.width,
            height: renderedSize.height,
            transform: `translate(calc(-50% + ${pan.x}px), calc(-50% + ${pan.y}px))`,
            transformOrigin: 'center center',
          }}
        >
          <img
            key={`${resetKey ?? 'default'}:${displayImageUrl}`}
            ref={imageElementRef}
            src={displayImageUrl}
            alt=""
            draggable={false}
            onLoad={(event: React.SyntheticEvent<HTMLImageElement>) => {
              applyLoadedImageSize(event.currentTarget, displayImageUrl)
            }}
            onError={() => {
              if (displayImageUrl !== imageUrl) {
                setLoadedImage(null)
                setImageLoadFailed(false)
                setUseFullSizeImage(false)
                return
              }
              setLoadedImage(null)
              setImageLoadFailed(true)
            }}
            style={{
              width: renderedSize.width,
              height: renderedSize.height,
              display: 'block',
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          />
          {naturalSize &&
            boxes.map(box => {
              const rect = getRenderedBoxRect(box, naturalSize, renderedSize)
              const isSpotlight = box.variant === 'spotlight'
              return (
                <Box
                  key={box.id}
                  sx={{
                    position: 'absolute',
                    left: rect.left,
                    top: rect.top,
                    width: rect.width,
                    height: rect.height,
                    border: `${box.borderWidth || 2}px solid ${box.color || '#ff6b2c'}`,
                    borderRadius: box.borderRadius ?? 4,
                    backgroundColor: isSpotlight
                      ? 'transparent'
                      : box.fillColor ?? `${box.color || '#ff6b2c'}22`,
                    boxShadow: isSpotlight
                      ? `0 0 0 9999px ${box.spotlightOverlayColor || 'rgba(15, 23, 42, 0.45)'}`
                      : 'none',
                    pointerEvents:
                      box.onMouseEnter || box.onMouseLeave ? 'auto' : 'none',
                    cursor: box.cursor ?? 'default',
                  }}
                  onMouseEnter={box.onMouseEnter}
                  onMouseLeave={box.onMouseLeave}
                />
              )
            })}
          {renderImageOverlay?.(renderContext)}
        </Box>
        {renderViewportOverlay?.(renderContext)}
        {imageLoadFailed ? (
          <Box
            sx={theme => ({
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              textAlign: 'center',
              color: theme.palette.text.secondary,
              p: 3,
            })}
          >
            <Typography variant="body2">{imageLoadFailedMessage}</Typography>
          </Box>
        ) : null}
        {!naturalSize && !imageLoadFailed && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
    )
  },
)

PanZoomImageViewer.displayName = 'PanZoomImageViewer'

export default PanZoomImageViewer
